import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Award } from "lucide-react";

export default function BadgesShelf({ earned = [], compact = false, showLocked = false }) {
  const [all, setAll] = useState([]);

  useEffect(() => { api.get("/badges").then((r) => setAll(r.data)).catch(() => {}); }, []);

  const earnedSet = new Set(earned);
  const earnedList = all.filter((b) => earnedSet.has(b.id));
  const locked = all.filter((b) => !earnedSet.has(b.id));

  const tierRing = (tier) => tier === "gold" ? "ring-[color:var(--bee-primary)]" : tier === "silver" ? "ring-[#9CA3AF]" : "ring-[#B77B3F]";

  if (compact) {
    return (
      <div className="flex flex-wrap gap-1.5" data-testid="badges-shelf-compact">
        {earnedList.length === 0 && <span className="text-xs text-[color:var(--bee-muted)] italic">No badges yet</span>}
        {earnedList.slice(0, 6).map((b) => (
          <div key={b.id} title={`${b.name} — ${b.desc}`} data-testid={`badge-chip-${b.id}`}
               className={`w-7 h-7 hex-clip ring-2 ${tierRing(b.tier)} bg-white flex items-center justify-center text-sm`}>
            {b.emoji}
          </div>
        ))}
        {earnedList.length > 6 && <span className="text-xs text-[color:var(--bee-muted)]">+{earnedList.length - 6}</span>}
      </div>
    );
  }

  return (
    <div data-testid="badges-shelf">
      <div className="flex items-baseline gap-3 mb-3">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Award size={18} /> Badges</h3>
        <span className="text-xs text-[color:var(--bee-muted)]">{earnedList.length} earned · {all.length} total</span>
      </div>
      {earnedList.length === 0 ? (
        <p className="text-sm text-[color:var(--bee-muted)] italic">No badges yet — start posting, joining subjects and making friends to earn them.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-4">
          {earnedList.map((b) => (
            <div key={b.id} data-testid={`badge-${b.id}`} className="text-center group cursor-help" title={b.desc}>
              <div className={`w-14 h-14 mx-auto hex-clip ring-2 ${tierRing(b.tier)} bg-white flex items-center justify-center text-2xl group-hover:scale-110 transition-transform`}>
                {b.emoji}
              </div>
              <div className="mt-1.5 text-[11px] font-semibold leading-tight">{b.name}</div>
              <div className="text-[9px] uppercase tracking-wider text-[color:var(--bee-muted)]">{b.tier}</div>
            </div>
          ))}
        </div>
      )}

      {showLocked && locked.length > 0 && (
        <details className="mt-2">
          <summary className="text-xs text-[color:var(--bee-muted)] cursor-pointer hover:text-[color:var(--bee-text)]">
            Show {locked.length} locked badge{locked.length !== 1 && "s"}
          </summary>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mt-3 opacity-50">
            {locked.map((b) => (
              <div key={b.id} className="text-center" title={b.desc}>
                <div className="w-14 h-14 mx-auto hex-clip bg-[color:var(--bee-surface-alt)] border border-[color:var(--bee-border)] flex items-center justify-center text-2xl grayscale">
                  {b.emoji}
                </div>
                <div className="mt-1.5 text-[11px] font-semibold leading-tight">{b.name}</div>
                <div className="text-[9px] text-[color:var(--bee-muted)] mt-0.5 line-clamp-2 px-1">{b.desc}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
